import React, { useState, useEffect } from 'react';
import { X, Loader, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getClientIpAddress } from '../utils/ipAddressService';

interface FirstLoginAgreementModalProps {
  userId: string;
  userEmail: string;
  userFullName: string | null;
  onAccept: () => void;
  onDecline: () => void;
}

const FirstLoginAgreementModal: React.FC<FirstLoginAgreementModalProps> = ({
  userId,
  userEmail,
  userFullName,
  onAccept,
  onDecline,
}) => {
  const [agreementContent, setAgreementContent] = useState<string>('');
  const [documentId, setDocumentId] = useState<string>('');
  const [documentVersion, setDocumentVersion] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [inputName, setInputName] = useState<string>(userFullName || '');
  const [inputEmail, setInputEmail] = useState<string>(userEmail || '');

  useEffect(() => {
    fetchAgreement();
  }, []);

  const fetchAgreement = async () => {
    try {
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('document_type', 'full_agreement')
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setAgreementContent(data.content);
        setDocumentId(data.id);
        setDocumentVersion(data.version);
      } else {
        setError('Legal agreement not found. Please contact support.');
      }
    } catch (err: any) {
      console.error('Error fetching agreement:', err);
      setError('Failed to load legal agreement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!accepted) return;

    if (!inputName.trim()) {
      setError('Please enter your full name');
      return;
    }

    if (!inputEmail.trim()) {
      setError('Please enter your email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inputEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setAccepting(true);
    setError(null);

    try {
      const ipAddress = await getClientIpAddress();

      const { error: acceptanceError } = await supabase
        .from('legal_acceptances')
        .insert({
          user_id: userId,
          document_id: documentId,
          document_type: 'full_agreement',
          document_version: documentVersion,
          ip_address: ipAddress,
          user_email: inputEmail.trim(),
          full_name: inputName.trim(),
          acceptance_context: 'first_login'
        });

      if (acceptanceError) throw acceptanceError;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          legal_agreement_version_accepted: documentVersion,
          force_agreement_acceptance: false
        })
        .eq('id', userId);

      if (updateError) throw updateError;

      onAccept();
    } catch (err: any) {
      console.error('Error accepting agreement:', err);
      setError('Failed to record acceptance. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-lg p-8 max-w-2xl w-full mx-4">
          <div className="flex items-center justify-center">
            <Loader className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-3 text-white">Loading legal agreement...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-white">User Agreement Required</h2>
          <p className="text-gray-300 mt-2">
            Please read and accept this agreement to continue using PlexyMusic
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-900 bg-opacity-50 border border-red-600 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-red-200">{error}</span>
            </div>
          )}

          <div className="prose prose-invert max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-200 leading-relaxed">
              {agreementContent}
            </pre>
          </div>
        </div>

        <div className="p-6 border-t border-gray-700 bg-gray-850">
          <div className="space-y-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={inputName}
                onChange={(e) => setInputName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full px-4 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={inputEmail}
                onChange={(e) => setInputEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full px-4 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <label className="flex items-start space-x-3 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 w-5 h-5 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
            />
            <span className="text-gray-200 text-sm font-medium">
              I have read and accept the User Music Upload & Public Performance Agreement
            </span>
          </label>

          <div className="flex justify-end space-x-3">
            <button
              onClick={onDecline}
              disabled={accepting}
              className="px-6 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Decline & Logout
            </button>
            <button
              onClick={handleAccept}
              disabled={!accepted || accepting}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {accepting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                'Accept & Continue'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstLoginAgreementModal;
